import { z } from 'zod';

/**
 * Recherche globale — une seule boîte pour tout l'atelier.
 *
 * L'application a une recherche par écran, ce qui suppose de savoir d'avance où
 * chercher. Or on part rarement d'un écran : on part d'un nom au téléphone,
 * d'un numéro de bon sur un sachet, d'une référence sur une facture. Cette
 * recherche répond à « où est cette chose », pas à « filtre cette liste » — les
 * filtres de chaque écran restent le bon outil pour explorer.
 */

export const TYPES_RESULTAT = ['CLIENT', 'COMMANDE', 'VENTE', 'FACTURE', 'PRODUIT'] as const;
export type TypeResultat = (typeof TYPES_RESULTAT)[number];

export const TYPE_RESULTAT_LABELS: Record<TypeResultat, string> = {
  CLIENT: 'Clients',
  COMMANDE: 'Commandes',
  VENTE: 'Ventes',
  FACTURE: 'Factures',
  PRODUIT: 'Stock',
};

/** En dessous de deux caractères, on ramènerait la moitié du fichier. */
export const RECHERCHE_MIN = 2;

/** Par type, pour que la liste reste lisible d'un coup d'œil. */
export const RECHERCHE_LIMITE_PAR_TYPE = 5;

export const rechercheQuerySchema = z.object({
  q: z.string().trim().min(RECHERCHE_MIN, 'Tapez au moins deux caractères'),
});
export type RechercheQuery = z.infer<typeof rechercheQuerySchema>;

export type ResultatRecherche = {
  type: TypeResultat;
  id: string;
  /** Ce qu'on lit en premier : le nom, la référence. */
  libelle: string;
  /** La ligne grise en dessous. */
  description: string | null;
  /** À droite : montant, quantité, date — ce qui distingue deux homonymes. */
  complement: string | null;
};

export type Recherche = {
  q: string;
  resultats: ResultatRecherche[];
};

/**
 * Où mène un résultat.
 *
 * Les commandes et les ventes n'ont pas d'écran à elles : leur détail s'ouvre
 * en tiroir depuis la liste, que `?id=` déplie directement. Le stock n'a pas
 * non plus de fiche : on y arrive filtré sur le nom du produit.
 */
export function lienResultat(r: ResultatRecherche): string {
  switch (r.type) {
    case 'CLIENT':
      return `/clients/${r.id}`;
    case 'COMMANDE':
      return `/commandes?id=${encodeURIComponent(r.id)}`;
    case 'VENTE':
      return `/ventes?id=${encodeURIComponent(r.id)}`;
    case 'FACTURE':
      return `/factures/${r.id}`;
    case 'PRODUIT':
      return `/stock?q=${encodeURIComponent(r.libelle)}`;
  }
}

/** Regroupe en conservant l'ordre de `TYPES_RESULTAT`. */
export function grouperResultats(
  resultats: readonly ResultatRecherche[],
): { type: TypeResultat; resultats: ResultatRecherche[] }[] {
  return TYPES_RESULTAT.map((type) => ({
    type,
    resultats: resultats.filter((r) => r.type === type),
  })).filter((g) => g.resultats.length > 0);
}
